import { addError } from '@infra/datadog/tracking';
import { DeviceIdService } from '@services/DeviceIdService';
import createClient from 'openapi-fetch';
import type { paths, components } from '@api/club-streams/generated';

import { ApiError } from '@libs/errors';
import { SignalRSocket } from '@libs/socket-signalr';

import { AuthService } from '@features/auth/services/AuthService';

const NAME = 'StreamApi';

/*
  ____,-------------------------------,____
  \   |          Http Client          |   /
  /___|-------------------------------|___\
*/

export { components, paths };

const api = createClient<paths>({
  baseUrl: __RUNTIME.clubApi,
});

api.use({
  async onRequest({ request }) {
    request.headers.set('X-Device-Id', DeviceIdService.instance.id);
  },

  async onResponse({ response }) {
    if (response.status >= 400) {
      const err = await ApiError.fromResponse(NAME, response);
      addError(err);
      throw err;
    }

    return undefined;
  },
  onError({ error, request }) {
    const err = ApiError.fromRequestError(NAME, request, error);
    addError(err);
    throw err;
  },
});

const { GET, POST } = api;
export { GET, POST };

/*
  ____,-------------------------------,____
  \   |         Socket Clients        |   /
  /___|-------------------------------|___\
*/

// ------------------------------------------
// Game public stream

// Совместим с GameAPI
export const GAME_STREAM_SOCKET_URL = `${__RUNTIME.clubWs}/hubs/stream/game`;

// ------------------------------------------
// Tournament public stream

// Совместим с GameAPI
export const TOURNAMENT_STREAM_SOCKET_URL = `${__RUNTIME.clubWs}/hubs/stream/tournament`;

// ------------------------------------------
// Round public stream with short games dtos

const ROUND_SHORT_GAMES_SOCKET_URL = `${__RUNTIME.clubWs}/hubs/stream/round`;

export type RoundShortGamesSocketEvents =
  | components['schemas']['RoundGamesStateShort']
  | components['schemas']['GameChangedEventShort'];

export class RoundShortGamesSocket extends SignalRSocket<RoundShortGamesSocketEvents> {
  constructor(
    public tournamentId: string,
    public roundNumber: number,
  ) {
    const EVENT_TYPES: Record<RoundShortGamesSocketEvents['eventType'], null> = {
      RoundGamesStateShort: null,
      GameChangedShort: null,
    };

    const u = new URL(ROUND_SHORT_GAMES_SOCKET_URL);
    u.searchParams.append('tournamentId', tournamentId);
    u.searchParams.append('roundNumber', roundNumber.toString());

    if (AuthService.instance.user?.userId) {
      u.searchParams.append('userId', AuthService.instance.user?.userId);
    }

    super(u.toString(), Object.keys(EVENT_TYPES), {});
  }
}

// ------------------------------------------
// Club public stream

// Совместим с ClubAPI (требует параметр ?clubId=ID)
export const CLUB_STREAM_SOCKET_URL = `${__RUNTIME.clubWs}/hubs/stream/club`;
